"use client";

import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

const TIMEFRAME_OPTIONS = [
  {
    value: "2_weeks",
    labelKey: "cancellation.pauseTimeframe.twoWeeks" as const,
  },
  {
    value: "1_month",
    labelKey: "cancellation.pauseTimeframe.oneMonth" as const,
  },
  {
    value: "2_months",
    labelKey: "cancellation.pauseTimeframe.coupleMonths" as const,
  },
  {
    value: "next_semester",
    labelKey: "cancellation.pauseTimeframe.nextSemester" as const,
  },
  {
    value: "not_sure",
    labelKey: "cancellation.pauseTimeframe.notSure" as const,
  },
] as const;

interface PauseTimeframeStepProps {
  onPause: (timeframe: string) => void;
  onContinueCanceling: () => void;
  isPausing: boolean;
  isCanceling: boolean;
}

export const PauseTimeframeStep = ({
  onPause,
  onContinueCanceling,
  isPausing,
  isCanceling,
}: PauseTimeframeStepProps) => {
  const t = useI18n();
  const [selectedTimeframe, setSelectedTimeframe] = useState<string | null>(
    null,
  );

  const isLoading = isPausing || isCanceling;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        {t("cancellation.pauseTimeframe.description")}
      </p>

      <div className="flex flex-col gap-2">
        {TIMEFRAME_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setSelectedTimeframe(option.value)}
            className={cn(
              "rounded-lg border p-3.5 text-left text-sm transition-colors cursor-pointer",
              selectedTimeframe === option.value
                ? "border-brand-primary bg-brand-primary/5 text-foreground"
                : "border-border text-foreground hover:border-muted-foreground/30",
            )}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 w-full">
        <Button
          onClick={() => selectedTimeframe && onPause(selectedTimeframe)}
          disabled={!selectedTimeframe || isLoading}
          className="w-full"
        >
          {isPausing && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("cancellation.pauseTimeframe.cta")}
        </Button>
        <Button
          variant="dangerOutline"
          onClick={onContinueCanceling}
          disabled={isLoading}
          className="w-full"
        >
          {isCanceling && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("cancellation.thankYou.stillCancel")}
        </Button>
      </div>
    </div>
  );
};
